import styles from "./StockFundamentals.module.scss";
import { Stock } from "@/models/stock";
import { FC, SetStateAction, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { classNames, nFormatter } from "@/lib/utils";
import { fundamentalsDataPointsByKeys } from "@/lib/vars";
import { getStockFundamentalsInfo } from "@/controllers/stock-fundamentals";
import { Column, useTable } from "react-table";
import { Column as ColumnModel } from "@/models/table";
import { StockFundamentals as StockFundamentalsType, StockFundamentalsData, StockFudamentalsHiglightedColumn } from "@/models/stock-fundamentals";
import type { CategoryChartOptions } from "@ortex-charts/react";
import { CategoryBarChart } from "@/components/ortex-charts/Viz";
import Module from "@/components/module/Module";
import { isUndefined } from "lodash";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";
import { themeState, positiveState, negativeState, backgroundColorState, textColorState, greyState, darkerBackgroundColorState } from "@/lib/store";
import { alpha } from "@/lib/ortex-charts/color";
import UpgradePlan from "../upgrade-plan/UpgradePlan";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faChevronsLeft, faChevronsRight } from "@fortawesome/pro-solid-svg-icons";
import Skeleton from "../skeleton/Skeleton";
import { Box, IconButton, Typography } from "@mui/material";
import ShowOnChart from "./ShowOnChart";
import { KeyboardArrowUp } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ObfuscationBanner from "../data-grid/ObfuscationBanner";

const ignoredColumns = ["summary", "show on chart", "trend"];
const regex = /[0-9]{4}(?:\sq[1-4])?e?$/gm;

let chartTimeout: NodeJS.Timeout;

interface PropsTypes {
    children?: ReactNode;
    stock: Stock;
    module: string;
    range: string;
    setRange: (range: string) => void;
    setParentObfuscated?: (value: SetStateAction<boolean>) => void;
    helpModuleId?: string;
    onStartTour?: () => void;
}

const StockFundamentals: FC<PropsTypes> = ({ stock, module, range, setRange, children, setParentObfuscated, helpModuleId, onStartTour }) => {
    const theme = useAtomValue(themeState);
    const grey = useAtomValue(greyState);
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const textColor = useAtomValue(textColorState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const darkerBackgroundColor = useAtomValue(darkerBackgroundColorState);

    const chartContainerRef = useRef<HTMLDivElement>(null);

    const [chartSeries, setChartSeries] = useState<Array<{ dataType: string; financial: [string, number][]; estimated: [string, number][] }>>([]);
    const dataTypes = useRef<string[]>(["Revenue"]);
    const isMounted = useRef<boolean>(false);
    const [period, setPeriod] = useState(dayjs());
    const tsShortened: boolean = false;

    const [loading, setLoading] = useState<boolean>(true);
    const [obfuscated, setObfuscated] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [showRowOnChart, setShowRowOnChart] = useState<Record<string, string>>({
        "Accounts Payable": "Show",
        "Accounts Receivable": "Show",
        "Accrued Expenses": "Show",
        "Accumulated Depreciation": "Show",
        "Additional Paid In Capital": "Show",
        "Asset Turnover": "Show",
        "Capital Expenditures": "Show",
        "Cash Ratio": "Show",
        "Cash and Cash Equivalents": !["summary", "balance"].includes(module) ? "Hide" : "Show",
        "Cashflow from Financing": "Show",
        "Cashflow from Investing": "Show",
        "Cashflow from Operation": "Show",
        "Common Stock": "Show",
        "Cost of Revenues": "Show",
        "Current Assets": "Hide",
        "Current Portion of LT Debt": "Show",
        "Current Ratio": "Show",
        "D&A": "Show",
        "Days Payable Outstanding": "Show",
        "Days Sales Outstanding": "Show",
        "Debt/Asset": "Show",
        "Debt/Capital": "Show",
        "Debt/EBITDA": "Show",
        "Debt/Equity": "Show",
        "Deferred Tax Assets": "Show",
        "Diluted Average Shares": "Show",
        "Diluted EPS": "Show",
        "EBIT Margin": "Show",
        "EBIT/Interest Expense": "Show",
        "EBITDA Margin %": "Show",
        "EBITDA Margin": "Show",
        "EBITDA/Interest Expense": "Show",
        EPS: "Show",
        "EV/EBITDA": "Show",
        "EV/Gross Profit": "Show",
        "EV/Revenue": "Show",
        "Earnings Before Tax": "Show",
        "Enterprise Value": "Show",
        "Gross Profit Margin %": "Show",
        "Gross Profit Margin": "Hide",
        "Gross Profit": "Show",
        "Interest Exp": "Show",
        "Interest Income": "Show",
        "Inventory Turnover": "Show",
        "Long Term Assets": "Show",
        "Long Term Debt": "Show",
        "Long Term Leases": "Show",
        "Market Cap": "Show",
        "Net Debt/EBITDA": "Show",
        "Net Income Margin %": "Show",
        "Net Income Margin": "Show",
        "Net Income": "Show",
        "Net PP&E": "Show",
        "Normalized Net Income Margin": "Show",
        "Normalized Net Income": "Hide",
        "Number of Employees": "Show",
        "Operating Income": "Show",
        "Other Intangibles": "Show",
        "Other Opt Exp": "Show",
        "Prepaid Expenses": "Show",
        "Price/Earnings": "Show",
        "Property Plant and Equipment": "Show",
        "Quick Ratio": "Show",
        "R&D": "Show",
        "Receivables Turnover": "Show",
        "Research and Development": "Show",
        "Retained Earnings": "Show",
        "Return on Assets": "Show",
        "Return on Capital": "Show",
        "Return on Equity": "Show",
        "Revenue Growth % YoY": "Show",
        "SG&A Margin": "Show",
        "SG&A": "Show",
        "Short term Investments": "Show",
        "Tax Provision": "Show",
        "Total Assets": "Show",
        "Total Current Assets": "Show",
        "Total Current Liabilities": "Show",
        "Total Equity": "Show",
        "Total Liabilites": "Show",
        "Total Operating Exp": "Show",
        "Total Revenues": "Hide",
        "Treasury Stock": "Show",
        Debt: "Show",
        EBITDA: "Show",
        EV: "Show",
        Goodwill: "Show",
        Inventory: "Show",
        Revenue: "Hide",
        "Depreciation and Amortization": "Show",
        "Stock Based Compensation": "Show",
        "Other Operating Activities": "Show",
        "Change in Accounts Receivable": "Show",
        "Change in Inventories": "Show",
        "Change in Accounts Payable": "Show",
        "Change in Unearned Revenues": "Show",
        "Change in Other Net Operating Assets": "Show",
        "Cash From Operations": "Show",
        "Capital Expenditure": "Show",
        "Sale of Property Plant and Equipment": "Show",
        "Cash Acquisitions": "Show",
        Divestitures: "Show",
        "Investment in Marketable Secutities": "Show",
        "Other Investing Activities": "Show",
        "Cash from Investing": "Show",
        "Cash from Financing": "Show",
        "Payments for Dividends": "Show",
        "Debt Issued": "Show",
        "Debt Repaid": "Show",
        "Issuance of Common Stock": "Show",
        "Other Financing Activities": "Show",
        "EV/Sales (LTM)": "Hide",
        "EV/EBIT (LTM)": "Show",
        "Price/Sales (LTM)": "Show",
        "Price/Earnings (LTM)": "Show",
        "Price/Book Value": "Show",
        "Price/Tangible Book Value": "Show",
    });

    const [chartData, setChartData] = useState<StockFundamentalsType[]>([]);
    const [rawData, setRawData] = useState<StockFundamentalsType[]>([]);
    const [rawColumns, setRawColumns] = useState<(ColumnModel & { key: string })[]>([]);
    const [highlightedColumn, setHighlightedColumn] = useState<StockFudamentalsHiglightedColumn>(undefined);

    const [noData, setNoData] = useState<boolean>(false);

    const handlePeriod = (type: "back" | "next", range: "year" | "quarter") => {
        setPeriod(period.add(type === "back" ? -1 : 1, range));
    };

    const getDefaultDataTypes = () => {
        switch (module) {
            case "summary":
                dataTypes.current = ["Revenue"];
                break;
            case "income":
                dataTypes.current = ["Total Revenues"];
                break;
            case "balance":
                dataTypes.current = ["Current Assets"];
                break;
            case "cash":
                dataTypes.current = ["Normalized Net Income"];
                break;
            case "valuation":
                dataTypes.current = ["EV/Sales (LTM)"];
                break;
            case "ratios":
                dataTypes.current = ["Gross Profit Margin"];
                break;
        }
    };

    const data = useMemo(
        () =>
            rawData.map((row) => {
                const dataObject = {};
                rawColumns.forEach((header) => {
                    dataObject[header.key] = row[header.list_position];
                });
                return dataObject;
            }),
        [rawData]
    );

    const columns = useMemo<Column<any>[]>(
        () =>
            rawColumns.map<Column<any>>((column, index) => ({
                Header: () => (
                    <div>
                        {range === "q" && index === 2 && (
                            <button type="button" aria-label="Back one year" onClick={obfuscated ? undefined : () => handlePeriod("back", "year")} style={{ backgroundColor: textColor }}>
                                <FontAwesomeIcon icon={faChevronsLeft} height={14} color={backgroundColor} />
                            </button>
                        )}
                        {index === 2 && (
                            <button
                                type="button"
                                aria-label={range === "q" ? "Back one quarter" : "Back one year"}
                                onClick={obfuscated ? undefined : () => handlePeriod("back", range === "q" ? "quarter" : "year")}
                                style={{ backgroundColor: textColor }}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} height={14} color={backgroundColor} />
                            </button>
                        )}
                        {index === rawColumns.length - 1 && (
                            <button
                                type="button"
                                aria-label={range === "q" ? "Forward one quarter" : "Forward one year"}
                                onClick={obfuscated ? undefined : () => handlePeriod("next", range === "q" ? "quarter" : "year")}
                                style={{ backgroundColor: textColor }}
                            >
                                <FontAwesomeIcon icon={faChevronRight} height={14} color={backgroundColor} />
                            </button>
                        )}
                        {range === "q" && index === rawColumns.length - 1 && (
                            <button type="button" aria-label="Forward one year" onClick={obfuscated ? undefined : () => handlePeriod("next", "year")} style={{ backgroundColor: textColor }}>
                                <FontAwesomeIcon icon={faChevronsRight} height={14} color={backgroundColor} />
                            </button>
                        )}
                        <p style={{ textAlign: ["show on chart", "trend"].includes(column.key) ? "center" : "right" }}>{column.name}</p>
                    </div>
                ),
                accessor: column.key,
                Cell: (cell) => {
                    switch (column.key) {
                        case "summary":
                            return cell.value;
                        case "show on chart":
                            return <ShowOnChart obfuscated={obfuscated} fetchChartData={fetchChartData} showRowOnChart={showRowOnChart} setShowRowOnChart={setShowRowOnChart} cell={cell} setIsExpanded={setIsExpanded} />;
                        case "trend":
                            const row = Object.entries(cell.row.values)
                                .filter((entry) => !ignoredColumns.includes(entry[0]))
                                .map((entry) => ({ y: Number(entry[1]), color: Number(entry[1]) < 0 ? negative : positive }));
                            return (
                                <div className={styles.trendCell}>
                                    <CategoryBarChart
                                        options={{
                                            categories: row.map((_, k) => String(k)),
                                            series: [{ key: "trend", data: row.map((p) => p.y) }],
                                            colorBySign: true,
                                            legend: false,
                                            tooltip: false,
                                            padding: 0,
                                            barPadding: 0.15,
                                            valueAxis: { visible: false, gridlines: false, format: { type: "compact" }, position: "left" },
                                            categoryAxis: { visible: false, maxLabelWidth: 10 },
                                        }}
                                        height={30}
                                        style={{ width: 75 }}
                                    />
                                </div>
                            );
                        default:
                            return cell.value ? nFormatter(cell.value, 2) : "-";
                    }
                },
            })),
        [period, rawColumns, theme]
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data, disableFilters: true, disableSortBy: true });

    const filterTypeOfData = (type: string, obj: {}) => {
        return Object.entries(obj)
            .filter(([key]) => {
                return (type === "financial" ? !key.endsWith("e") : key.endsWith("e")) && !ignoredColumns.includes(key);
            })
            .map(([key, value]) => {
                if (key.endsWith("e")) {
                    return [key.slice(0, -1).toUpperCase(), Number(value)];
                }
                return [key.toUpperCase(), Number(value)];
            });
    };

    const fetchChartData = (index?: number) => {
        clearTimeout(chartTimeout);
        const fundamentalsInfo: StockFundamentalsData[] = chartData.map((row) => {
            const dataObject: StockFundamentalsData = {};
            rawColumns.forEach((header) => {
                dataObject[header.key] = row[header.list_position];
            });
            return dataObject;
        });
        if (isFinite(index)) {
            const dataType = fundamentalsInfo[index].summary;
            if (dataTypes.current.includes(dataType)) dataTypes.current = dataTypes.current.filter((dt) => dt !== dataType);
            else dataTypes.current.push(dataType);
        }
        setObfuscated((obf) => obfuscated || obf);
        setParentObfuscated && setParentObfuscated((obf) => obfuscated || obf);
        // One chart per selected line: each fundamental has its own scale, and
        // reported figures sit beside the estimates for the same periods.
        setChartSeries(
            dataTypes.current
                .map((dataType) => fundamentalsInfo.find((el) => el.summary === dataType))
                .filter(Boolean)
                .map((el) => ({ dataType: el.summary, financial: filterTypeOfData("financial", el) as [string, number][], estimated: filterTypeOfData("estimated", el) as [string, number][] }))
        );
    };

    const chartOptionsFor = (item: { dataType: string; financial: [string, number][]; estimated: [string, number][] }): Partial<CategoryChartOptions> => {
        const categories = Array.from(new Set([...item.financial.map(([k]) => k), ...item.estimated.map(([k]) => k)]));
        const lookup = (rows: [string, number][]) => categories.map((c) => rows.find(([k]) => k === c)?.[1] ?? NaN);
        return {
            categories,
            series: [
                { key: "financials", title: `${item.dataType}`, data: lookup(item.financial), color: positive },
                { key: "estimates", title: `${item.dataType} (estimates)`, data: lookup(item.estimated), color: alpha(positive, 0.45) },
            ],
            legend: true,
            barPadding: 0.2,
            valueAxis: { position: "left", visible: true, gridlines: true, format: { type: "compact", precision: 2 } },
            categoryAxis: { visible: true, maxLabelWidth: 70 },
            valueLabels: false,
        };
    };

    useEffect(() => {
        setLoading(true);

        const params =
            isMounted.current && period
                ? {
                      from_period: range === "a" ? period.subtract(3, "years").year() : `${period.subtract(1, "year").year()}Q${period.subtract(1, "year").quarter()}`,
                      family: module,
                      revamp: true,
                  }
                : { family: module, revamp: true };

        getStockFundamentalsInfo(stock.stock_id, range, {
            ...params,
        })
            .then((data) => {
                setObfuscated((obf) => obf || data.obfuscated);
                setParentObfuscated && setParentObfuscated((obf) => obfuscated || obf);
                setChartData(data.rows);

                if (data.rows.length === 0 || !Array.isArray(data.rows[0])) {
                    // If the data is not in the expected format, set noData to true
                    setNoData(true);
                    return;
                } else if (data.rows.every((row) => row.slice(1).every((cell) => cell === "" || cell === null || cell === undefined))) {
                    // If all data cells are empty, set noData to true
                    setNoData(true);
                    return;
                } else setNoData(false);

                const columns = Object.entries<any>(data.table)
                    .filter((entry) => {
                        const periodColumn = entry[0].match(regex);
                        if (!periodColumn) return entry[1].display;
                        const periodValue = periodColumn[0].split("e");
                        return !isUndefined(periodValue[0]);
                    })
                    .sort((a, b) => a[1].order - b[1].order)
                    .map((entry) => ({ key: entry[0], ...entry[1] }));

                setRawColumns(columns);
                setHighlightedColumn(columns.find((column) => column.hasOwnProperty("highlighted")));

                setRawData(data.rows);
            })
            .finally(() => {
                setLoading(false);
                isMounted.current = true;
            });

        return () => {
            setObfuscated(false);
            setParentObfuscated && setParentObfuscated(false);
        };
    }, [range, period]);

    useEffect(() => {
        getDefaultDataTypes();
    }, [module]);

    useEffect(() => {
        fetchChartData();
    }, [data]);

    return (
        <div className={styles.StockFundamentals}>
            <style jsx>
                {`
                    .${styles["summary-table"]} {
                        height: ${!isExpanded ? "90%" : "55%"};
                    }

                    .${styles["summary-chart"]} {
                        height: ${!isExpanded ? "10%" : "45%"};
                    }

                    .${styles["summary-table"]} .${styles["content"]} {
                        height: 90%;
                    }

                    tr > th {
                        background-color: ${backgroundColor};
                    }

                    tr {
                        background-color: ${darkerBackgroundColor};
                    }

                    tr:nth-child(even) {
                        background-color: ${backgroundColor};
                    }

                    @media screen and (min-width: 1440px) {
                        .${styles["summary-table"]} .${styles["content"]} {
                            height: calc(100% - 45px);
                        }
                    }
                    @media screen and (min-width: 900px) and (max-width: 1440px) {
                        .${styles["summary-table"]} .${styles["content"]} {
                            height: 90%;
                        }
                    }
                    @media screen and (min-width: 768px) and (max-width: 900px) {
                        .${styles["summary-table"]} .${styles["content"]} {
                            height: ${!isExpanded ? "95%" : "90%"};
                        }
                    }
                `}
            </style>
            <div className={styles["summary-table"]}>
                <Module help={{ moduleId: helpModuleId || "fundamentals", modulePage: "V2_stock_fundamentals" }} noHeader>
                    <Box sx={{ height: "100%" }}>
                        <div className={styles.children}>{children}</div>
                        {loading ? (
                            <div style={{ backgroundColor }} className={styles.loading}>
                                <Skeleton variant="rounded" width="100%" height="100%" loading={loading} />
                            </div>
                        ) : (
                            <div className={styles.content}>
                                {noData ? (
                                    <div className={styles.noData}>
                                        <Typography color={textColor}>No data available</Typography>
                                    </div>
                                ) : (
                                    <div>
                                        {obfuscated ? <ObfuscationBanner theme={theme} /> : null}
                                        <div className={styles.table}>
                                            <div>
                                                <table {...getTableProps()}>
                                                    <thead>
                                                        {headerGroups.map((headerGroup) => (
                                                            <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                                                                {headerGroup.headers.map((header) => (
                                                                    <th key={header.id} {...header.getHeaderProps()} className={styles[header.id === highlightedColumn?.key && "highlight"]}>
                                                                        {header.render("Header")}
                                                                    </th>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </thead>
                                                    <tbody {...getTableBodyProps()} className={obfuscated && "obfuscated"}>
                                                        {rows.map((row) => {
                                                            prepareRow(row);
                                                            return (
                                                                <tr key={row.id} {...row.getRowProps()}>
                                                                    {row.cells.map((cell) => (
                                                                        <td
                                                                            key={cell.column.id}
                                                                            {...cell.getCellProps()}
                                                                            className={classNames(
                                                                                ignoredColumns.includes(cell.column.id) || !cell.value ? undefined : cell.value < 0 ? "negative" : "positive",
                                                                                cell.column.id === highlightedColumn?.key ? styles.highlight : undefined
                                                                            )}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    justifyContent: ["show on chart", "trend"].includes(cell.column.id) ? "center" : cell.column.id === "name" ? "flex-start" : "flex-end",
                                                                                }}
                                                                            >
                                                                                {cell.render("Cell")}
                                                                            </div>
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Box>
                </Module>
            </div>
            <div className={styles["summary-chart"]}>
                <Module
                    title={"Fundamentals Chart"}
                    help={{ moduleId: helpModuleId || "fundamentals", modulePage: "V2_stock_fundamentals" }}
                    helpActions={onStartTour ? [{ label: "Restart the guided tour", onClick: onStartTour }] : undefined}
                    allowFullscreen={isExpanded}
                    settingsComponent={
                        <>
                            <IconButton
                                onClick={() => setIsExpanded(!isExpanded)}
                                aria-label={isExpanded ? "Collapse fundamentals chart" : "Expand fundamentals chart"}
                                aria-expanded={isExpanded}
                                sx={{ width: "fit-content", color: textColor, fill: textColor }}
                            >
                                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                        </>
                    }
                >
                    <div className={classNames(styles.chart, obfuscated && !tsShortened && "obfuscated")} style={{ display: !isExpanded ? "none" : "initial" }} ref={chartContainerRef}>
                        {tsShortened && <UpgradePlan />}

                        <div className={styles.chartsStack}>
                            {!obfuscated && <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{`${stock.exchangesymbol}:${stock.ticker} - Fundamentals`}</Typography>}
                            {chartSeries.map((item) => (
                                <div key={item.dataType} className={styles.chartItem}>
                                    <Typography variant="caption" sx={{ color: grey }}>
                                        {fundamentalsDataPointsByKeys[item.dataType] ?? item.dataType}
                                    </Typography>
                                    <CategoryBarChart options={chartOptionsFor(item)} height={chartSeries.length > 1 ? 180 : 320} />
                                </div>
                            ))}
                        </div>
                    </div>
                </Module>
            </div>
        </div>
    );
};

export default StockFundamentals;
